import axios from 'axios';
import { config } from '../config/environment';

// Déclaration pour import.meta.env
declare global {
  interface ImportMeta {
    env: {
      VITE_API_URL: string;
      [key: string]: any;
    }
  }
}

// Création d'une instance axios avec la configuration de base
const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
  // Envoyer les cookies httpOnly (nécessaire pour le refreshToken)
  withCredentials: true,
});

// Intercepteur pour ajouter le token d'authentification aux requêtes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// FormData : ne pas forcer application/json — le navigateur envoie le boundary multipart
api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    const h = config.headers as { delete?: (name: string) => void; 'Content-Type'?: string };
    if (typeof h?.delete === 'function') {
      h.delete('Content-Type');
    } else if (h && 'Content-Type' in h) {
      delete h['Content-Type'];
    }
  }
  return config;
});

// Flag pour éviter une boucle infinie si le refresh lui-même échoue
let isRefreshing = false;
// File d'attente des requêtes en attente du nouveau token
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

function processQueue(error: any, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token as string);
    }
  });
  failedQueue = [];
}

// Intercepteur pour gérer les réponses et les erreurs
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Erreur réseau (pas de réponse du serveur)
    if (!error.response) {
      console.error('Erreur réseau:', error.message);
      return Promise.reject({
        message: 'Erreur de connexion au serveur. Veuillez vérifier votre connexion internet.',
        originalError: error
      });
    }

    const status = error.response.status;
    const errorData = error.response.data;
    const originalRequest = error.config;

    // Tentative de rafraîchissement du token sur TOKEN_EXPIRED
    // On n'essaie pas de rafraîchir si c'est déjà la route /refresh elle-même
    if (
      status === 401 &&
      errorData?.code === 'TOKEN_EXPIRED' &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/auth/refresh')
    ) {
      if (isRefreshing) {
        // Une requête de refresh est déjà en cours : mettre en file d'attente
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Appel refresh — le cookie httpOnly refreshToken est envoyé automatiquement
        const refreshResponse = await axios.post(
          `${config.apiUrl}/api/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newAccessToken = refreshResponse.data?.data?.accessToken;

        if (!newAccessToken) {
          throw new Error('Aucun token reçu du serveur de refresh');
        }

        // Mettre à jour le token dans le localStorage et le store Pinia
        localStorage.setItem('token', newAccessToken);

        // Mettre à jour le store Pinia sans import circulaire
        // On passe par un event custom que le store peut écouter
        window.dispatchEvent(new CustomEvent('token-refreshed', { detail: { token: newAccessToken } }));

        // Rejouer toutes les requêtes en attente avec le nouveau token
        processQueue(null, newAccessToken);

        // Rejouer la requête originale
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);

        // Le refresh a échoué : déconnecter l'utilisateur
        localStorage.removeItem('token');
        localStorage.removeItem('user-data');
        localStorage.removeItem('last-activity');
        localStorage.removeItem('user-role');
        window.dispatchEvent(new CustomEvent('auth-session-expired'));

        return Promise.reject({
          message: 'Session expirée. Veuillez vous reconnecter.',
          status: 401,
          code: 'SESSION_EXPIRED',
          originalError: refreshError
        });
      } finally {
        isRefreshing = false;
      }
    }

    // Autres erreurs 401 (mauvais credentials, token invalide, etc.)
    if (status === 401) {
      console.warn('Erreur 401:', errorData);
      return Promise.reject({
        message: errorData?.message || 'Session expirée. Veuillez vous reconnecter.',
        status: 401,
        code: errorData?.code,
        originalError: error,
        response: error.response
      });
    }

    // Autres 4xx (400 validation, 409 conflit, 429 throttling…) : garder response + errors
    if (status >= 400 && status < 500 && status !== 401) {
      return Promise.reject({
        message: errorData?.message || 'Une erreur est survenue',
        code: errorData?.code,
        errors: errorData?.errors,
        status,
        response: error.response,
        originalError: error
      });
    }

    if (status >= 500) {
      return Promise.reject({
        message: 'Une erreur serveur est survenue. Veuillez réessayer plus tard.',
        code: errorData?.code,
        status,
        response: error.response,
        originalError: error
      });
    }

    return Promise.reject({
      message: errorData?.message || 'Une erreur est survenue',
      code: errorData?.code,
      response: error.response,
      originalError: error
    });
  }
);

// Exporter l'instance api
export { api };

// Interfaces de base
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: User;
}
